import React, { useEffect, useMemo, useState } from "react";
import { Link, Navigate, useLocation, useNavigate } from "react-router-dom";
import {
  AlertCircle,
  CreditCard,
  LayoutDashboard,
  Loader2,
  LogOut,
  RefreshCw,
  Save,
  Settings,
  ShieldCheck,
  UserRound,
} from "lucide-react";
import { memberApi } from "../api/member";
import AccountSecurityForms from "../Components/AccountSecurityForms";
import HouseholdEditor, {
  cleanHouseholdRows,
  householdSizeValue,
} from "../Components/HouseholdEditor";
import { pageRoutes } from "../utils";

const tabs = [
  { path: pageRoutes.MemberDashboard, label: "Overview", icon: LayoutDashboard },
  { path: pageRoutes.MemberDashboardProfile, label: "Profile", icon: UserRound },
  { path: pageRoutes.MemberDashboardMembership, label: "Membership", icon: ShieldCheck },
  { path: pageRoutes.MemberDashboardPayments, label: "Payments", icon: CreditCard },
  { path: pageRoutes.MemberDashboardSettings, label: "Settings", icon: Settings },
];

function formatCurrency(amount, currency = "usd") {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: currency.toUpperCase(),
  }).format((Number(amount) || 0) / 100);
}

function formatDate(value) {
  if (!value) {
    return "Not available";
  }
  return new Intl.DateTimeFormat("en-US", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date(value));
}

function StatusBadge({ value }) {
  const normalized = String(value || "unknown").replace(/_/g, " ");
  const active = ["active", "paid", "not required"].includes(normalized);
  return (
    <span
      className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${
        active ? "bg-emerald-50 text-emerald-800" : "bg-amber-50 text-amber-800"
      }`}
    >
      {normalized}
    </span>
  );
}

export default function MemberDashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const [status, setStatus] = useState("loading");
  const [errorMessage, setErrorMessage] = useState("");
  const [dashboard, setDashboard] = useState(null);
  const [summary, setSummary] = useState(null);
  const [needsProfile, setNeedsProfile] = useState(false);
  const [payments, setPayments] = useState([]);
  const [profileForm, setProfileForm] = useState({});
  const [householdForm, setHouseholdForm] = useState({
    householdSize: "1",
    householdMembers: [],
  });
  const [saveStatus, setSaveStatus] = useState("idle");
  const [householdSaveStatus, setHouseholdSaveStatus] = useState("idle");
  const [retryStatus, setRetryStatus] = useState("idle");

  const activePath = useMemo(() => {
    const match = [...tabs]
      .sort((a, b) => b.path.length - a.path.length)
      .find((tab) => location.pathname === tab.path);
    return match?.path || pageRoutes.MemberDashboard;
  }, [location.pathname]);

  useEffect(() => {
    let cancelled = false;

    async function loadDashboard() {
      try {
        // Only /api/member/me decides whether the session is valid. The other
        // three are tolerated individually so one failing panel cannot blank
        // the dashboard and read as a logout.
        const [memberData, paymentData, householdData, summaryData] = await Promise.all([
          memberApi("/api/member/me"),
          memberApi("/api/member/payments").catch(() => null),
          memberApi("/api/member/household").catch(() => null),
          // Contribution standing derived from Stripe-confirmed records.
          memberApi("/api/member/membership-summary").catch(() => null),
        ]);
        if (cancelled) {
          return;
        }
        setDashboard(memberData);
        setSummary(summaryData);
        setPayments(paymentData?.payments || []);
        setProfileForm(memberData.profile || {});
        setNeedsProfile(
          Boolean(householdData?.requiresProfileCompletion) || !memberData.profile
        );
        setHouseholdForm({
          householdSize: householdSizeValue(
            householdData?.profile?.householdSize,
            householdData?.householdMembers?.length || 0
          ),
          householdMembers: householdData?.householdMembers || [],
        });
        setStatus("ready");
      } catch (error) {
        if (cancelled) {
          return;
        }
        if (error instanceof Error && /authentication required/i.test(error.message)) {
          navigate(pageRoutes.MemberLogin, {
            replace: true,
            state: { from: location },
          });
          return;
        }
        setErrorMessage(
          error instanceof Error
            ? error.message
            : "Unable to load your member dashboard."
        );
        setStatus("error");
      }
    }

    loadDashboard();
    return () => {
      cancelled = true;
    };
  }, [location, navigate]);

  function updateProfile(field, value) {
    setProfileForm((current) => ({ ...current, [field]: value }));
  }

  function updateHouseholdSize(value) {
    setHouseholdForm((current) => ({ ...current, householdSize: value }));
  }

  function updateHouseholdMembers(householdMembers) {
    setHouseholdForm((current) => ({ ...current, householdMembers }));
  }

  async function saveProfile(event) {
    event.preventDefault();
    setSaveStatus("loading");
    setErrorMessage("");
    try {
      const payload = await memberApi("/api/member/profile", {
        method: "PATCH",
        body: JSON.stringify(profileForm),
      });
      setDashboard((current) => ({ ...current, profile: payload.profile }));
      setProfileForm(payload.profile || {});
      setSaveStatus("saved");
    } catch (error) {
      setSaveStatus("error");
      setErrorMessage(
        error instanceof Error ? error.message : "Unable to save your profile."
      );
    }
  }

  async function saveHousehold(event) {
    event.preventDefault();
    setHouseholdSaveStatus("loading");
    setErrorMessage("");
    try {
      const householdMembers = cleanHouseholdRows(
        householdForm.householdMembers
      );
      const payload = await memberApi("/api/member/household", {
        method: "PATCH",
        body: JSON.stringify({
          householdSize: householdForm.householdSize,
          householdMembers,
        }),
      });
      const nextSize = householdSizeValue(
        payload.profile?.householdSize,
        payload.householdMembers?.length || 0
      );
      setDashboard((current) => ({
        ...current,
        profile: { ...current.profile, householdSize: payload.profile.householdSize },
      }));
      setHouseholdForm({
        householdSize: nextSize,
        householdMembers: payload.householdMembers || [],
      });
      setHouseholdSaveStatus("saved");
    } catch (error) {
      setHouseholdSaveStatus("error");
      setErrorMessage(
        error instanceof Error ? error.message : "Unable to save household details."
      );
    }
  }

  async function logout() {
    await memberApi("/api/auth/logout", { method: "POST" });
    navigate(pageRoutes.MemberLogin, { replace: true });
  }

  async function retryPayment() {
    setRetryStatus("loading");
    setErrorMessage("");
    try {
      const payload = await memberApi("/api/member/membership/retry-payment", {
        method: "POST",
        body: JSON.stringify({}),
      });
      window.location.assign(payload.checkoutUrl);
    } catch (error) {
      setRetryStatus("error");
      setErrorMessage(
        error instanceof Error
          ? error.message
          : "Unable to restart membership payment."
      );
    }
  }

  if (location.pathname === "/members/dashboard/") {
    return <Navigate to={pageRoutes.MemberDashboard} replace />;
  }

  if (status === "loading") {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#FDF8F3]">
        <Loader2 className="h-8 w-8 animate-spin text-[#722F37]" />
      </div>
    );
  }

  if (status === "error") {
    return (
      <div className="min-h-screen bg-[#FDF8F3] px-4 py-16">
        <div className="mx-auto max-w-xl rounded-3xl border border-red-200 bg-white p-8 text-red-900">
          <AlertCircle className="mb-4 h-8 w-8" />
          <p>{errorMessage}</p>
        </div>
      </div>
    );
  }

  const { user, profile, membership } = dashboard;
  // `contribution` is the canonical derived object; `payment` is the older
  // alias, kept so this page still renders if the backend rollout lags.
  const contribution = summary?.contribution || summary?.payment || null;
  const registrationChoice = summary?.registrationPaymentChoice || null;
  const fullName = [profile?.firstName, profile?.lastName].filter(Boolean).join(" ");
  const canRetry =
    membership &&
    (["pending_payment", "failed", "canceled", "expired"].includes(
      membership.membershipStatus
    ) ||
      ["pending", "failed", "canceled"].includes(membership.paymentStatus));

  return (
    <div className="min-h-screen bg-[#FDF8F3]">
      <section className="bg-[#722F37] px-4 py-12 text-white">
        <div className="mx-auto flex max-w-6xl flex-col gap-6 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <p className="text-sm font-semibold uppercase tracking-[0.22em] text-[#E7D6A8]">
              Member Dashboard
            </p>
            <h1 className="mt-3 font-serif-elegant text-4xl font-bold">
              Welcome, {fullName || user.email}
            </h1>
            <p className="mt-2 max-w-2xl text-white/75">
              Manage your household profile, membership status, and Stripe-confirmed
              membership payment records.
            </p>
          </div>
          <button
            type="button"
            onClick={logout}
            className="inline-flex items-center justify-center gap-2 rounded-xl border border-white/30 px-4 py-2 text-sm font-semibold hover:bg-white/10"
          >
            <LogOut className="h-4 w-4" />
            Sign out
          </button>
        </div>
      </section>

      <div className="mx-auto grid max-w-6xl gap-8 px-4 py-10 lg:grid-cols-[240px_1fr]">
        <nav className="space-y-2">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            const active = activePath === tab.path;
            return (
              <Link
                key={tab.path}
                to={tab.path}
                className={`flex items-center gap-3 rounded-xl px-4 py-3 text-sm font-semibold ${
                  active
                    ? "bg-[#722F37] text-white"
                    : "bg-white text-gray-700 hover:bg-[#722F37]/10"
                }`}
              >
                <Icon className="h-4 w-4" />
                {tab.label}
              </Link>
            );
          })}
        </nav>

        <div className="space-y-6">
          {needsProfile && (
            <div className="flex items-start gap-2 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
              <AlertCircle className="mt-0.5 h-4 w-4 flex-none" />
              <span>
                Your profile details are not set up yet. You are signed in — open the
                Profile tab to add your name and contact information.
              </span>
            </div>
          )}
          {errorMessage && (
            <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-900">
              {errorMessage}
            </div>
          )}

          {activePath === pageRoutes.MemberDashboard && (
            <div className="grid gap-5 md:grid-cols-3">
              <div className="rounded-2xl border border-[#C9A227]/20 bg-white p-6">
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-gray-500">
                  Account
                </p>
                <p className="mt-3 text-lg font-semibold text-[#722F37]">
                  {user.email}
                </p>
                <StatusBadge value={user.status} />
              </div>
              <div className="rounded-2xl border border-[#C9A227]/20 bg-white p-6">
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-gray-500">
                  Membership
                </p>
                <p className="mt-3 text-lg font-semibold text-[#722F37]">
                  {membership?.membershipType?.replace(/_/g, " ") || "Not set"}
                </p>
                <StatusBadge value={membership?.membershipStatus} />
              </div>
              <div className="rounded-2xl border border-[#C9A227]/20 bg-white p-6">
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-gray-500">
                  Payment
                </p>
                <p className="mt-3 text-lg font-semibold text-[#722F37]">
                  {payments[0]
                    ? formatCurrency(payments[0].amount, payments[0].currency)
                    : "No payments yet"}
                </p>
                <StatusBadge value={payments[0]?.status || membership?.paymentStatus} />
              </div>
            </div>
          )}

          {activePath === pageRoutes.MemberDashboardProfile && (
            <div className="space-y-6">
              <form
                onSubmit={saveProfile}
                className="rounded-3xl border border-[#C9A227]/20 bg-white p-6"
              >
                <h2 className="font-serif-elegant text-2xl font-bold text-[#722F37]">
                  Profile
                </h2>
                <div className="mt-6 grid gap-5 sm:grid-cols-2">
                  {[
                    ["firstName", "First name"],
                    ["lastName", "Last name"],
                    ["phone", "Phone"],
                    ["familyName", "Family or baptism name"],
                    ["addressLine1", "Address line 1"],
                    ["addressLine2", "Address line 2"],
                    ["city", "City"],
                    ["stateRegion", "State or region"],
                    ["postalCode", "Postal code"],
                    ["preferredLanguage", "Preferred language"],
                  ].map(([field, label]) => (
                    <label key={field} className="block">
                      <span className="mb-2 block text-sm font-medium text-gray-700">
                        {label}
                      </span>
                      <input
                        value={profileForm[field] || ""}
                        onChange={(event) => updateProfile(field, event.target.value)}
                        className="w-full rounded-xl border border-gray-300 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#C9A227]"
                      />
                    </label>
                  ))}
                </div>
                <button
                  type="submit"
                  disabled={saveStatus === "loading"}
                  className="mt-6 inline-flex items-center gap-2 rounded-xl bg-[#722F37] px-5 py-3 font-semibold text-white hover:bg-[#5A252C] disabled:opacity-70"
                >
                  {saveStatus === "loading" ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Save className="h-4 w-4" />
                  )}
                  {saveStatus === "saved" ? "Saved" : "Save profile"}
                </button>
              </form>

              <HouseholdEditor
                householdSize={householdForm.householdSize}
                householdMembers={householdForm.householdMembers}
                onHouseholdSizeChange={updateHouseholdSize}
                onHouseholdMembersChange={updateHouseholdMembers}
                onSubmit={saveHousehold}
                saveStatus={householdSaveStatus}
              />
            </div>
          )}

          {activePath === pageRoutes.MemberDashboardMembership && (
            <div className="rounded-3xl border border-[#C9A227]/20 bg-white p-6">
              <h2 className="font-serif-elegant text-2xl font-bold text-[#722F37]">
                Membership
              </h2>
              <dl className="mt-6 grid gap-4 sm:grid-cols-2">
                <div>
                  <dt className="text-sm text-gray-500">Membership status</dt>
                  <dd>
                    <StatusBadge value={membership?.membershipStatus} />
                  </dd>
                </div>
                <div>
                  <dt className="text-sm text-gray-500">Contribution status</dt>
                  <dd className="font-semibold text-gray-900">
                    {contribution?.statusLabel || "Not available"}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm text-gray-500">Contribution type</dt>
                  <dd className="font-semibold text-gray-900">
                    {contribution?.typeLabel ||
                      membership?.membershipType?.replace(/_/g, " ") ||
                      "Not set"}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm text-gray-500">Latest contribution</dt>
                  <dd className="font-semibold text-gray-900">
                    {contribution?.latestPaidAt
                      ? `${formatCurrency(
                          contribution.latestAmount,
                          contribution.currency
                        )} on ${formatDate(contribution.latestPaidAt)}`
                      : "None recorded"}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm text-gray-500">Total recorded contributions</dt>
                  <dd className="font-semibold text-gray-900">
                    {formatCurrency(
                      contribution?.totalPaid || 0,
                      contribution?.currency
                    )}
                    {contribution?.contributionCount
                      ? ` across ${contribution.contributionCount} payment${
                          contribution.contributionCount === 1 ? "" : "s"
                        }`
                      : ""}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm text-gray-500">Stripe confirmed</dt>
                  <dd className="font-semibold text-gray-900">
                    {contribution?.isStripeConfirmed ? "Yes" : "No"}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm text-gray-500">Member since</dt>
                  <dd className="font-semibold text-gray-900">
                    {formatDate(membership?.createdAt)}
                  </dd>
                </div>
                {registrationChoice?.label && (
                  <div>
                    <dt className="text-sm text-gray-500">
                      Registration payment choice
                    </dt>
                    <dd className="font-semibold text-gray-900">
                      {registrationChoice.label}
                    </dd>
                    <dd className="mt-1 text-xs text-gray-500">
                      Historical. This is what you chose when you registered.
                    </dd>
                  </div>
                )}
              </dl>

              {contribution?.incompleteAttemptCount > 0 && (
                <p className="mt-4 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
                  You have {contribution.incompleteAttemptCount} checkout attempt
                  {contribution.incompleteAttemptCount === 1 ? "" : "s"} that were
                  started but never completed. They are not counted as contributions.
                </p>
              )}

              <p className="mt-4 text-sm text-gray-600">
                Payment status is updated automatically from Stripe-confirmed records.
                It cannot be edited here. If something looks wrong, contact the parish
                office.
              </p>

              <div className="mt-5 flex flex-wrap gap-3">
                <Link
                  to={pageRoutes.Donations}
                  className="inline-flex items-center gap-2 rounded-xl bg-[#722F37] px-5 py-3 font-semibold text-white hover:bg-[#5A252C]"
                >
                  {contribution?.status === "active_recurring"
                    ? "Make an additional contribution"
                    : "Make a contribution"}
                </Link>
                {summary?.actions?.canManageRecurring && (
                  <span className="inline-flex items-center rounded-xl border border-[#C9A227]/40 px-4 py-3 text-sm text-gray-700">
                    To change or stop recurring giving, contact the parish office.
                  </span>
                )}
              </div>
              {canRetry && (
                <button
                  type="button"
                  onClick={retryPayment}
                  disabled={retryStatus === "loading"}
                  className="mt-6 inline-flex items-center gap-2 rounded-xl bg-[#C9A227] px-5 py-3 font-semibold text-[#4b1d23] hover:bg-[#b69322] disabled:opacity-70"
                >
                  {retryStatus === "loading" ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <RefreshCw className="h-4 w-4" />
                  )}
                  Continue secure Stripe Checkout
                </button>
              )}
            </div>
          )}

          {activePath === pageRoutes.MemberDashboardPayments && (
            <div className="rounded-3xl border border-[#C9A227]/20 bg-white p-6">
              <h2 className="font-serif-elegant text-2xl font-bold text-[#722F37]">
                Payment History
              </h2>
              <div className="mt-6 overflow-x-auto">
                <table className="w-full min-w-[640px] text-left text-sm">
                  <thead className="border-b text-xs uppercase tracking-[0.16em] text-gray-500">
                    <tr>
                      <th className="py-3">Date</th>
                      <th className="py-3">Purpose</th>
                      <th className="py-3">Amount</th>
                      <th className="py-3">Status</th>
                      <th className="py-3">Receipt</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {payments.length === 0 ? (
                      <tr>
                        <td colSpan="5" className="py-6 text-gray-500">
                          No membership payments are recorded yet.
                        </td>
                      </tr>
                    ) : (
                      payments.map((payment) => (
                        <tr key={payment.id}>
                          <td className="py-4">{formatDate(payment.paidAt || payment.createdAt)}</td>
                          <td className="py-4">{payment.purpose?.replace(/_/g, " ")}</td>
                          <td className="py-4 font-semibold">
                            {formatCurrency(payment.amount, payment.currency)}
                          </td>
                          <td className="py-4">
                            <StatusBadge value={payment.status} />
                          </td>
                          <td className="py-4">
                            {payment.stripeReceiptUrl ? (
                              <a
                                className="font-semibold text-[#722F37] underline"
                                href={payment.stripeReceiptUrl}
                                target="_blank"
                                rel="noreferrer"
                              >
                                Stripe receipt
                              </a>
                            ) : (
                              <span className="text-gray-500">Stripe receipt email</span>
                            )}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {activePath === pageRoutes.MemberDashboardSettings && (
            <div className="space-y-6">
              <div className="rounded-3xl border border-[#C9A227]/20 bg-white p-6">
                <h2 className="font-serif-elegant text-2xl font-bold text-[#722F37]">
                  Account settings
                </h2>
                <p className="mt-3 text-sm text-gray-600">
                  Update your password or request an email change. Email changes are
                  completed only after the new address is verified.
                </p>
              </div>
              <AccountSecurityForms loginPath={pageRoutes.MemberLogin} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
